/**
 * ---------------------------------------------------------
 * Graph Layout Engine
 * ---------------------------------------------------------
 *
 * Converts
 *      Nodes
 *      +
 *      Transitions
 *
 * into ReactFlow Nodes & Edges.
 *
 * Temporary layout only.
 * Positions are calculated on every render.
 *
 * Supports:
 *  ✔ Multiple Roots
 *  ✔ Branches
 *  ✔ Merge Nodes
 *  ✔ Loop Back
 * ---------------------------------------------------------
 */

const HORIZONTAL_GAP = 340;
const VERTICAL_GAP = 220;

export function calculateGraphLayout(nodes = [], transitions = []) {
    const validNodes = (Array.isArray(nodes) ? nodes : [])
        .filter((node) => node?.id !== undefined && node?.id !== null);

    if (!validNodes.length) {
        return {
            nodes: [],
            edges: [],
        };
    }

    /*
    |--------------------------------------------------------------------------
    | Maps
    |--------------------------------------------------------------------------
    */

    const nodeMap = new Map();

    validNodes.forEach((node) => {
        nodeMap.set(String(node.id), node);
    });

    // A stage canvas only receives that stage's nodes. Pipeline transitions may
    // point to a node in another stage, so exclude those edges before traversal.
    const visibleTransitions = (Array.isArray(transitions) ? transitions : []).filter((transition) => {
        if (!transition) return false;

        return (
            nodeMap.has(String(transition.from_node_id)) &&
            nodeMap.has(String(transition.to_node_id))
        );
    });

    /*
    |--------------------------------------------------------------------------
    | Graph
    |--------------------------------------------------------------------------
    */

    const outgoing = new Map();
    const incomingCount = new Map();

    validNodes.forEach((node) => {
        const nodeId = String(node.id);
        outgoing.set(nodeId, []);
        incomingCount.set(nodeId, 0);
    });

    visibleTransitions.forEach((transition) => {
        const fromNodeId = String(transition.from_node_id);
        const toNodeId = String(transition.to_node_id);

        outgoing
            .get(fromNodeId)
            .push(toNodeId);

        incomingCount.set(
            toNodeId,
            (incomingCount.get(toNodeId) || 0) + 1
        );
    });

    /*
    |--------------------------------------------------------------------------
    | Root Nodes
    |--------------------------------------------------------------------------
    */

    const roots = validNodes.filter(
        (node) => (incomingCount.get(String(node.id)) || 0) === 0
    );

    /*
    |--------------------------------------------------------------------------
    | BFS
    |--------------------------------------------------------------------------
    */

    const queue = [];

    const visited = new Set();

    const levels = [];

    roots.forEach((root) => {

        queue.push({
            id: String(root.id),
            depth: 0,
        });

    });

    while (queue.length) {

        const current = queue.shift();

        if (visited.has(current.id)) {
            continue;
        }

        visited.add(current.id);

        if (!levels[current.depth]) {
            levels[current.depth] = [];
        }

        levels[current.depth].push(current.id);

        const children = outgoing.get(current.id) || [];

        children.forEach((childId) => {

            if (!visited.has(childId)) {

                queue.push({
                    id: childId,
                    depth: current.depth + 1,
                });

            }

        });

    }

    /*
    |--------------------------------------------------------------------------
    | Unreachable Nodes
    |--------------------------------------------------------------------------
    */

    validNodes.forEach((node) => {

        const nodeId = String(node.id);

        if (!visited.has(nodeId)) {

            if (!levels[0]) {
                levels[0] = [];
            }

            levels[0].push(nodeId);

        }

    });

    /*
    |--------------------------------------------------------------------------
    | ReactFlow Nodes
    |--------------------------------------------------------------------------
    */

    const flowNodes = [];

    levels.forEach((level, depth) => {

        const width =
            (level.length - 1) * HORIZONTAL_GAP;

        level.forEach((nodeId, index) => {

            const node = nodeMap.get(String(nodeId));

            if (!node) {
                return;
            }

            const x =
                index * HORIZONTAL_GAP - width / 2;

            const y =
                depth * VERTICAL_GAP;

            flowNodes.push({

                id: String(node.id),

                type: "workflowNode",

                position: {
                    x,
                    y,
                },

                data: {
                    node,
                },

            });

        });

    });

    /*
    |--------------------------------------------------------------------------
    | ReactFlow Edges
    |--------------------------------------------------------------------------
    */

    const flowEdges = visibleTransitions
        .map((transition, index) => ({
            id: transition.id !== undefined && transition.id !== null
                ? `edge-${transition.id}`
                : `edge-${transition.from_node_id}-${transition.to_node_id}-${index}`,

            source: String(transition.from_node_id),

            target: String(transition.to_node_id),

            type: "smoothstep",

            animated: false,

            markerEnd: {
                type: "arrowclosed",
            },

            style: {
                strokeWidth: 2,
            },

            label: transition.action_group_name || undefined,

            data: transition,
        }));

    return {

        nodes: flowNodes,

        edges: flowEdges,

    };
}
